import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { ExcelReportInfoBM } from 'src/types/BusinessModel/ExcelReportInfoBM';
import { EventTypeData } from 'src/types/ViewModel/EventTypeData';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';

export interface ReportGenerationRequest {
    reportType: REPORT_TYPE;
    data: ReportData | LastCargoesReportData | ManifoldPlanReportData | ManifestReportData | TankplanReportData;
    options?: ReportGenerationOptions;
}

export interface ReportGenerationResponse {
    success: boolean;
    reportBase64?: string;
    dataUri: string;
    mimeType: string;
    fileExtension: string;
    error?: string;
}

export interface ReportGenerationOptions {
    fileName: string;
    orientation?: 'portrait' | 'landscape';
    format?: string;
}
export interface ReportType {
    reportType: REPORT_TYPE;
    title: string;
    description: string;
}
export interface ReportSubType {
    reportType: REPORT_TYPE;
    subType: string;
    tankPlanCondition: TankPlanCondition;
    headerTitle?: string;
    icon: any;
    // Applies to Tank Plan and Manifest subtypes: 'port' subtypes are always scoped to the whole
    // port call (ignoring any berth selection), while 'berth' subtypes are scoped to whichever
    // berth is selected in the "Berth reports" tab's shared berth selector.
    reportScope?: 'port' | 'berth';
}

export enum REPORT_TYPE {
    TANK_PLANS = 'TANK_ PLANS',
    MANIFESTS = 'MANIFESTS',
    MANIFOLD_PLAN = 'MANIFOLD_PLAN',
    LAST_CARGOES = 'LAST_CARGOES',
    LAST_CARGOES_FOSFA = 'LAST_CARGOES_FOSFA',
    LAST_CARGOES_NOBL = 'LAST_CARGOES_NOBL',
    CARGO_EVENT = 'CARGO_EVENT',
    NOR_TENDER = 'NOR_TENDER',
    RE_TENDER_NOR = 'RE_TENDER_NOR',
    PROTEST_DIFF = 'PROTEST_DIFF',
    SLOW_RATE = 'SLOW_RATE',
    PROTEST_DEADFREIGHT_CLAIM = 'PROTEST_DEADFREIGHT_CLAIM',
    GENERAL_PROTEST = 'GENERAL_PROTEST',
    INSUFFICIEN_NUMBER_HOSES_REPORT = 'INSUFFICIEN_NUMBER_HOSES_REPORT',
    CERTIFICATE_OF_CLEANLINESS = 'CERTIFICATE_OF_CLEANLINESS',
    EMPTY_TANK_CERTIFICATE = 'EMPTY_TANK_CERTIFICATE',
    PROTEST_USE_OF_VESSEL_HOSES = 'PROTEST_USE_OF_VESSEL_HOSES',
    PROTEST_FREE_PRATIQUE = 'PROTEST_FREE_PRATIQUE',
}

export const ReportTypes: ReportType[] = [
    { reportType: REPORT_TYPE.TANK_PLANS, title: 'Tank plan reports', description: 'Tank plan reports' },
    { reportType: REPORT_TYPE.MANIFESTS, title: 'Manifest reports', description: 'Manifest reports' },
    { reportType: REPORT_TYPE.MANIFOLD_PLAN, title: 'Manifold plan', description: 'Manifold plan' },
    { reportType: REPORT_TYPE.LAST_CARGOES, title: 'Last cargoes', description: 'History of previous cargoes with details' },
    {
        reportType: REPORT_TYPE.LAST_CARGOES_FOSFA,
        title: 'Last cargoes – FOSFA',
        description: 'History of previous cargoes with FOSFA status highlighting',
    },
    {
        reportType: REPORT_TYPE.LAST_CARGOES_NOBL,
        title: 'Last cargo - NOBL',
        description: 'History of previous cargoes with Not On Banned List status highlighting',
    },
    { reportType: REPORT_TYPE.CARGO_EVENT, title: 'Port Events', description: 'Port Events' },
    { reportType: REPORT_TYPE.NOR_TENDER, title: 'Nor Tender', description: 'Nor Tender' },
    { reportType: REPORT_TYPE.RE_TENDER_NOR, title: 'Nor Tender', description: 'Nor Tender' },
    { reportType: REPORT_TYPE.PROTEST_DIFF, title: 'Protest Diff', description: 'Protest different figures' },
    { reportType: REPORT_TYPE.SLOW_RATE, title: 'Slow Rate', description: 'Protest slow rate' },
    { reportType: REPORT_TYPE.PROTEST_DEADFREIGHT_CLAIM, title: 'Protest deadfreight claim', description: 'Protest deadfreight claim' },
    { reportType: REPORT_TYPE.GENERAL_PROTEST, title: 'General protest', description: 'General protest' },
    {
        reportType: REPORT_TYPE.INSUFFICIEN_NUMBER_HOSES_REPORT,
        title: 'Insufficient Number Hoses Report',
        description: 'Insufficient Number Hoses Report',
    },
    {
        reportType: REPORT_TYPE.CERTIFICATE_OF_CLEANLINESS,
        title: 'Certificate Of Cleanliness',
        description: 'Certificate Of Cleanliness',
    },
    {
        reportType: REPORT_TYPE.EMPTY_TANK_CERTIFICATE,
        title: 'Empty Tank Certificate',
        description: 'Empty Tank Certificate',
    },
    {
        reportType: REPORT_TYPE.PROTEST_USE_OF_VESSEL_HOSES,
        title: 'Protest Use Of Vessel Hoses',
        description: 'Protest Use Of Vessel Hoses',
    },
    {
        reportType: REPORT_TYPE.PROTEST_FREE_PRATIQUE,
        title: 'Protest Free Pratique',
        description: 'Protest Free Pratique',
    },
];

export const ReportSubTypes: ReportSubType[] = [
    // Tank plan - port scope (Port reports tab), always the whole port call.
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Cargo loading tank plan',
        headerTitle: 'Tank plan - loading operations in',
        tankPlanCondition: TankPlanCondition.LoadingOperations,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Transit tank plan',
        headerTitle: 'Tank plan - transit through',
        tankPlanCondition: TankPlanCondition.TransitOperations,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Cargo unloading tank plan',
        headerTitle: 'Tank plan - unloading operations in',
        tankPlanCondition: TankPlanCondition.UnloadingOperations,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Arrival tank plan',
        headerTitle: 'Tank plan - arrival at',
        tankPlanCondition: TankPlanCondition.Arrival,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Departure tank plan',
        headerTitle: 'Tank plan - departure from',
        tankPlanCondition: TankPlanCondition.Departure,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    // Tank plan - berth scope (Berth reports tab), scoped to the shared berth selector.
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Cargo loading tank plan',
        headerTitle: 'Tank plan - loading operations in',
        tankPlanCondition: TankPlanCondition.LoadingOperations,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Transit tank plan',
        headerTitle: 'Tank plan - transit through',
        tankPlanCondition: TankPlanCondition.TransitOperations,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Cargo unloading tank plan',
        headerTitle: 'Tank plan - unloading operations in',
        tankPlanCondition: TankPlanCondition.UnloadingOperations,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Arrival tank plan',
        headerTitle: 'Tank plan - arrival at',
        tankPlanCondition: TankPlanCondition.Arrival,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.TANK_PLANS,
        subType: 'Departure tank plan',
        headerTitle: 'Tank plan - departure from',
        tankPlanCondition: TankPlanCondition.Departure,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    // Manifest - port scope (Port reports tab), always the whole port call.
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Cargo loading report',
        headerTitle: 'Loading operations in',
        tankPlanCondition: TankPlanCondition.LoadingOperations,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Transit cargo report',
        headerTitle: 'Transit cargo in',
        tankPlanCondition: TankPlanCondition.TransitOperations,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Cargo unloading report',
        headerTitle: 'Unloading operations in',
        tankPlanCondition: TankPlanCondition.UnloadingOperations,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Arrival manifest',
        headerTitle: 'Cargo manifest - arrival at',
        tankPlanCondition: TankPlanCondition.Arrival,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Departure manifest',
        headerTitle: 'Cargo manifest - departure from',
        tankPlanCondition: TankPlanCondition.Departure,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Operations in call',
        headerTitle: 'Operations in ',
        tankPlanCondition: TankPlanCondition.OperationsInCall,
        icon: 'file-pdf',
        reportScope: 'port',
    },
    // Manifest - berth scope (Berth reports tab), scoped to the shared berth selector.
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Cargo loading report',
        headerTitle: 'Loading operations in',
        tankPlanCondition: TankPlanCondition.LoadingOperations,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Transit cargo report',
        headerTitle: 'Transit cargo in',
        tankPlanCondition: TankPlanCondition.TransitOperations,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Cargo unloading report',
        headerTitle: 'Unloading operations in',
        tankPlanCondition: TankPlanCondition.UnloadingOperations,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Arrival manifest',
        headerTitle: 'Cargo manifest - arrival at',
        tankPlanCondition: TankPlanCondition.Arrival,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Departure manifest',
        headerTitle: 'Cargo manifest - departure from',
        tankPlanCondition: TankPlanCondition.Departure,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    {
        reportType: REPORT_TYPE.MANIFESTS,
        subType: 'Operations in call',
        headerTitle: 'Operations in ',
        tankPlanCondition: TankPlanCondition.OperationsInCall,
        icon: 'file-pdf',
        reportScope: 'berth',
    },
    { reportType: REPORT_TYPE.MANIFOLD_PLAN, subType: 'Manifold Plan', tankPlanCondition: null, icon: 'file-excel' },
    { reportType: REPORT_TYPE.LAST_CARGOES, subType: 'Last Cargoes', tankPlanCondition: null, icon: 'file-excel' },
    { reportType: REPORT_TYPE.LAST_CARGOES_FOSFA, subType: 'Last Cargo - Approved', tankPlanCondition: null, icon: 'file-excel' },
    { reportType: REPORT_TYPE.LAST_CARGOES_NOBL, subType: 'Last Cargo - NOBL', tankPlanCondition: null, icon: 'file-excel' },
];

// Decouples "tab" in the Generate Report modal from REPORT_TYPE: the Port reports and Berth
// reports tabs each combine a Tank plan section and a Manifest section (filtered by reportScope),
// while Manifold plan and Last cargoes remain single-section tabs (no header, no scope).
export enum REPORT_TAB {
    PORT_REPORTS = 'PORT_REPORTS',
    BERTH_REPORTS = 'BERTH_REPORTS',
    MANIFOLD_PLAN = 'MANIFOLD_PLAN',
    LAST_CARGOES = 'LAST_CARGOES',
}

export interface ReportTabSection {
    header?: string;
    reportType: REPORT_TYPE;
    // Optional: when a section needs to render subtypes from more than one REPORT_TYPE under a
    // single header (e.g. the FOSFA column combining "Last Cargo - Approved" and "Last Cargo -
    // NOBL"), list every report type here. Falls back to [reportType] when omitted.
    reportTypes?: REPORT_TYPE[];
    reportScope?: 'port' | 'berth';
}

export interface ReportTabConfig {
    tab: REPORT_TAB;
    title: string;
    sections: ReportTabSection[];
    // When true, a single shared berth selector is rendered at the top of the tab, and every
    // report option in every section of this tab requires a berth to be selected before it can
    // be checked.
    requiresBerthSelection?: boolean;
}

export const ReportTabs: ReportTabConfig[] = [
    {
        tab: REPORT_TAB.PORT_REPORTS,
        title: 'Port reports',
        sections: [
            { header: 'Tank plan', reportType: REPORT_TYPE.TANK_PLANS, reportScope: 'port' },
            { header: 'Manifest', reportType: REPORT_TYPE.MANIFESTS, reportScope: 'port' },
        ],
    },
    {
        tab: REPORT_TAB.BERTH_REPORTS,
        title: 'Berth reports',
        requiresBerthSelection: true,
        sections: [
            { header: 'Tank plan', reportType: REPORT_TYPE.TANK_PLANS, reportScope: 'berth' },
            { header: 'Manifest', reportType: REPORT_TYPE.MANIFESTS, reportScope: 'berth' },
        ],
    },
    {
        tab: REPORT_TAB.MANIFOLD_PLAN,
        title: 'Manifold plan',
        sections: [{ reportType: REPORT_TYPE.MANIFOLD_PLAN }],
    },
    {
        tab: REPORT_TAB.LAST_CARGOES,
        title: 'Last cargoes',
        sections: [
            { header: 'Last Cargoes', reportType: REPORT_TYPE.LAST_CARGOES },
            { header: 'FOSFA', reportType: REPORT_TYPE.LAST_CARGOES_FOSFA, reportTypes: [REPORT_TYPE.LAST_CARGOES_FOSFA, REPORT_TYPE.LAST_CARGOES_NOBL] },
        ],
    },
];

export const REPORT_TYPE_ORIENTATION: Record<REPORT_TYPE, 'portrait' | 'landscape'> = {
    [REPORT_TYPE.TANK_PLANS]: 'landscape',
    [REPORT_TYPE.MANIFESTS]: 'landscape',
    [REPORT_TYPE.MANIFOLD_PLAN]: 'portrait',
    [REPORT_TYPE.LAST_CARGOES]: 'landscape',
    [REPORT_TYPE.LAST_CARGOES_FOSFA]: 'landscape',
    [REPORT_TYPE.LAST_CARGOES_NOBL]: 'landscape',
    [REPORT_TYPE.CARGO_EVENT]: 'portrait',
    [REPORT_TYPE.NOR_TENDER]: 'portrait',
    [REPORT_TYPE.RE_TENDER_NOR]: 'portrait',
    [REPORT_TYPE.PROTEST_DIFF]: 'portrait',
    [REPORT_TYPE.SLOW_RATE]: 'portrait',
    [REPORT_TYPE.PROTEST_DEADFREIGHT_CLAIM]: 'portrait',
    [REPORT_TYPE.GENERAL_PROTEST]: 'portrait',
    [REPORT_TYPE.INSUFFICIEN_NUMBER_HOSES_REPORT]: 'portrait',
    [REPORT_TYPE.CERTIFICATE_OF_CLEANLINESS]: 'portrait',
    [REPORT_TYPE.EMPTY_TANK_CERTIFICATE]: 'portrait',
    [REPORT_TYPE.PROTEST_USE_OF_VESSEL_HOSES]: 'portrait',
    [REPORT_TYPE.PROTEST_FREE_PRATIQUE]: 'portrait',
};

export const REPORT_TYPE_FILE_FORMAT: Record<REPORT_TYPE, 'pdf' | 'xlsx'> = {
    [REPORT_TYPE.TANK_PLANS]: 'pdf',
    [REPORT_TYPE.MANIFESTS]: 'pdf',
    [REPORT_TYPE.MANIFOLD_PLAN]: 'xlsx',
    [REPORT_TYPE.LAST_CARGOES]: 'xlsx',
    [REPORT_TYPE.LAST_CARGOES_FOSFA]: 'xlsx',
    [REPORT_TYPE.LAST_CARGOES_NOBL]: 'xlsx',
    [REPORT_TYPE.CARGO_EVENT]: 'xlsx',
    [REPORT_TYPE.NOR_TENDER]: 'xlsx',
    [REPORT_TYPE.RE_TENDER_NOR]: 'xlsx',
    [REPORT_TYPE.PROTEST_DIFF]: 'xlsx',
    [REPORT_TYPE.SLOW_RATE]: 'xlsx',
    [REPORT_TYPE.PROTEST_DEADFREIGHT_CLAIM]: 'xlsx',
    [REPORT_TYPE.GENERAL_PROTEST]: 'xlsx',
    [REPORT_TYPE.INSUFFICIEN_NUMBER_HOSES_REPORT]: 'xlsx',
    [REPORT_TYPE.CERTIFICATE_OF_CLEANLINESS]: 'xlsx',
    [REPORT_TYPE.EMPTY_TANK_CERTIFICATE]: 'xlsx',
    [REPORT_TYPE.PROTEST_USE_OF_VESSEL_HOSES]: 'xlsx',
    [REPORT_TYPE.PROTEST_FREE_PRATIQUE]: 'xlsx',
};

export const REPORT_TYPE_HEADER: Record<REPORT_TYPE, string> = {
    [REPORT_TYPE.TANK_PLANS]: 'Tank plans',
    [REPORT_TYPE.MANIFESTS]: 'Manifests',
    [REPORT_TYPE.MANIFOLD_PLAN]: 'Manifold plan',
    [REPORT_TYPE.LAST_CARGOES]: 'Last cargoes',
    [REPORT_TYPE.LAST_CARGOES_FOSFA]: 'Last cargoes – FOSFA',
    [REPORT_TYPE.LAST_CARGOES_NOBL]: 'Last cargo – NOBL',
    [REPORT_TYPE.CARGO_EVENT]: 'Port Events',
    [REPORT_TYPE.NOR_TENDER]: 'Port Events',
    [REPORT_TYPE.RE_TENDER_NOR]: 'Port Events',
    [REPORT_TYPE.PROTEST_DIFF]: 'Port Events',
    [REPORT_TYPE.SLOW_RATE]: 'Port Events',
    [REPORT_TYPE.PROTEST_DEADFREIGHT_CLAIM]: 'Port Events',
    [REPORT_TYPE.GENERAL_PROTEST]: 'Port Events',
    [REPORT_TYPE.INSUFFICIEN_NUMBER_HOSES_REPORT]: 'Port Events',
    [REPORT_TYPE.CERTIFICATE_OF_CLEANLINESS]: 'Port Events',
    [REPORT_TYPE.EMPTY_TANK_CERTIFICATE]: 'Port Events',
    [REPORT_TYPE.PROTEST_USE_OF_VESSEL_HOSES]: 'Port Events',
    [REPORT_TYPE.PROTEST_FREE_PRATIQUE]: 'Port Events',
};

export interface ReportData {
    reportType: REPORT_TYPE;
    vessel: Vessel;
    selectedPortCall: RotationPortCall;
    selectedTankIds?: number[];
    headerTitle?: string;
    selectedCharterers?: string[];
}

export interface CargoContentsPerTank {
    tankId: number;
    cargoContent: CargoContent[];
}

export interface LastCargoesReportData extends ReportData {
    stowagePlan: StowagePlan;
    numberOfSteps: number;
    selectedBerthCall: RotationBerthCall;
    cargoContentsPerTank: CargoContentsPerTank[];
    cargoNameType?: string;
    includeUnNo?: boolean;
    includeAllTanks?: boolean;
    // Controls FOSFA-related filtering/coloring in the generated Excel report:
    // 'off' = plain Last Cargoes (no coloring/filtering), 'approved' = Last Cargo - Approved
    // (only acceptable 1st cargoes kept, unless includeAllTanks), 'nobl' = Last Cargo - NOBL
    // (banned 1st cargoes filtered out, unless includeAllTanks; acceptable/none both kept).
    fosfaMode?: 'off' | 'approved' | 'nobl';
}

export interface CargoEventReportData extends ReportData {
    tankersEvents: TankersEvent[];
    portCallId: number;
    vessel: Vessel;
    stowagePlan: StowagePlan;
    eventTypeData: EventTypeData;
    excelReportInfo: ExcelReportInfoBM;
}

export interface ManifoldPlanReportData extends ReportData {
    selectedBerthCall: RotationBerthCall;
    cargoContent: CargoContent[];
}
export interface ManifestReportData extends ReportData {
    selectedBerthCall: RotationBerthCall;
    cargoInTanksList: ManifestPdfReportContent[];
}

export interface TankplanReportData extends ReportData {
    vessel: Vessel;
    selectedPortCall: RotationPortCall;
    selectedBerthCall: RotationBerthCall;
    imgData: string;
    imgWidth: number;
    imgHeight: number;
}

export interface ReportOption {
    type: REPORT_TYPE;
    title: string;
    description: string;
    icon: any;
}

export interface ManifestPdfReportContent {
    cargoNumber: string;
    tradeName: string;
    operationType: string;
    shipFigure: string;
    billOfLadingFigure: string;
    litreWeight: string;
    flashPoint: string;
    heating: string;
    un: string;
    imdg: string;
    marpol: string;
    uscg: string;
    loadPorts: string;
    unloadingPorts: string;
    stowedInTanks: string;
}

export const reportOptions: ReportOption[] = [
    {
        type: REPORT_TYPE.TANK_PLANS,
        title: 'Cargo loading tank plan',
        description: 'Cargo loading tank plan',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.TANK_PLANS,
        title: 'Transit tank plan',
        description: 'Transit tank plan',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.TANK_PLANS,
        title: 'Cargo unloading tank plan',
        description: 'Cargo unloading tank plan',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.TANK_PLANS,
        title: 'Arrival tank plan',
        description: 'Arrival tank plan',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.TANK_PLANS,
        title: 'Departure tank plan',
        description: 'Departure tank plan',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.MANIFESTS,
        title: 'Manifests',
        description: 'Cargo loading report',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.MANIFESTS,
        title: 'Manifests',
        description: 'Transit cargo report',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.MANIFESTS,
        title: 'Manifests',
        description: 'Cargo unloading report',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.MANIFESTS,
        title: 'Manifests',
        description: 'Arrival manifest',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.MANIFESTS,
        title: 'Manifests',
        description: 'Operations in call manifest',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.MANIFESTS,
        title: 'Manifests',
        description: 'Departure manifest',
        icon: 'file-pdf',
    },
    {
        type: REPORT_TYPE.MANIFOLD_PLAN,
        title: 'Manifold Plan',
        description: 'Detailed manifold plan with connections and specifications',
        icon: 'file-excel',
    },
    {
        type: REPORT_TYPE.LAST_CARGOES,
        title: 'Last Cargoes',
        description: 'History of previous cargoes with details',
        icon: 'file-excel',
    },
    {
        type: REPORT_TYPE.LAST_CARGOES_FOSFA,
        title: 'Last Cargoes – FOSFA',
        description: 'History of previous cargoes with FOSFA status highlighting',
        icon: 'file-excel',
    },
];
